import * as React from 'react';
import { CheckIcon, ChevronDownIcon, XIcon } from 'lucide-react';
import { cn } from '../lib/utils';
import { Badge } from './ui/badge';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './ui/command';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

export interface MultiSelectOption {
  value: string;
  label: React.ReactNode;
  /** Shown in the trigger's chip. Defaults to `value`. */
  short?: React.ReactNode;
  keywords?: string;
}

export interface MultiSelectProps {
  value: string[];
  onChange: (value: string[]) => void;
  options: MultiSelectOption[];
  placeholder?: React.ReactNode;
  searchPlaceholder?: string;
  emptyText?: React.ReactNode;
  /** Further options are disabled once this many are chosen. */
  max?: number;
  /** Hide the search box, for short lists. */
  searchable?: boolean;
  disabled?: boolean;
  className?: string;
  id?: string;
  'aria-invalid'?: boolean;
}

/**
 * A multi-value select: chosen values as removable chips in the trigger, and a
 * checklist in the popover. The replacement for antd's `<Select mode="multiple">`.
 */
export const MultiSelect = React.forwardRef<HTMLButtonElement, MultiSelectProps>(
  function MultiSelect(
    {
      value,
      onChange,
      options,
      placeholder = 'Select…',
      searchPlaceholder = 'Search…',
      emptyText = 'No results',
      max,
      searchable = true,
      disabled = false,
      className,
      id,
      ...aria
    },
    ref,
  ) {
    const [open, setOpen] = React.useState(false);
    const selected = new Set(value);
    const atMax = max !== undefined && value.length >= max;

    const toggle = (next: string) => {
      if (selected.has(next)) onChange(value.filter((item) => item !== next));
      else if (!atMax) onChange([...value, next]);
    };

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            ref={ref}
            id={id}
            type="button"
            role="combobox"
            aria-expanded={open}
            aria-invalid={aria['aria-invalid']}
            disabled={disabled}
            className={cn(
              'flex min-h-9 w-full items-center justify-between gap-2 rounded-md border border-input bg-card px-3 py-1 text-start text-sm shadow-xs transition-[color,box-shadow] outline-none',
              'focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50',
              'disabled:cursor-not-allowed disabled:opacity-50',
              'aria-invalid:border-destructive aria-invalid:ring-destructive/20',
              className,
            )}
          >
            {value.length === 0 ? (
              <span className="truncate text-muted-foreground">{placeholder}</span>
            ) : (
              <span className="flex min-w-0 flex-wrap gap-1">
                {value.map((item) => {
                  const option = options.find((candidate) => candidate.value === item);
                  return (
                    <Badge key={item} variant="secondary" className="gap-0.5 pe-1">
                      {option?.short ?? item}
                      <span
                        role="button"
                        tabIndex={-1}
                        aria-label="Remove"
                        className="rounded-sm opacity-60 hover:opacity-100"
                        onPointerDown={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          toggle(item);
                        }}
                      >
                        <XIcon />
                      </span>
                    </Badge>
                  );
                })}
              </span>
            )}
            <ChevronDownIcon className="size-4 shrink-0 opacity-60" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-(--radix-popover-trigger-width) min-w-56 p-0">
          <Command>
            {searchable ? <CommandInput placeholder={searchPlaceholder} /> : null}
            <CommandList>
              <CommandEmpty>{emptyText}</CommandEmpty>
              <CommandGroup>
                {options.map((option) => {
                  const isSelected = selected.has(option.value);
                  return (
                    <CommandItem
                      key={option.value}
                      value={option.value}
                      keywords={[
                        option.keywords ?? (typeof option.label === 'string' ? option.label : ''),
                      ]}
                      disabled={!isSelected && atMax}
                      onSelect={() => toggle(option.value)}
                    >
                      <span
                        className={cn(
                          'flex size-4 items-center justify-center rounded-[4px] border border-input',
                          isSelected && 'border-primary bg-primary text-primary-foreground',
                        )}
                      >
                        {isSelected ? (
                          <CheckIcon className="size-3 text-current" strokeWidth={3} />
                        ) : null}
                      </span>
                      {option.label}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  },
);
